import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'reactstrap';
import { Button as MUIButton, TextField, Autocomplete } from '@mui/material';
import { getProductList, getStorageList } from 'api/trade_in';
import styles from '../TradeInFormModal.module.scss';
import PhoneIcon from 'assets/images/trade_in-Phone.png';
import TabletIcon from 'assets/images/trade_in-Tablet.png';
import WatchIcon from 'assets/images/trade_in-Watch.png';
import { notify } from 'utils/notifSender';

const Page1 = ({ page1Values, setPage1Values, setTradeInDevice }) => {
    const [compasiaProductList, setCompasiaProductList] = useState([]);
    const [storageList, setStorageList] = useState([]);

    // to obtain available Categories from CompAsia's product list
    const productsByCategories = compasiaProductList.reduce((acc, product) => {
        if (!acc[product.categoryName]) acc[product.categoryName] = [];

        acc[product.categoryName].push(product);

        return acc;
    }, {});
    const categories = Object.keys(productsByCategories).map((category) => {
        const products = productsByCategories[category];
        const highestGradeAPrice = Math.max(...products.map((product) => product.GradeAPrice));

        const image = category === 'Smartphone' ? PhoneIcon : category === 'Tablet' ? TabletIcon : WatchIcon;

        return {
            name: category,
            image: image,
            discount: highestGradeAPrice
        };
    });

    // to obtain available Brands based on selected category
    const productsBySelectedCategory = productsByCategories[page1Values.selectedCategory] || [];
    const brandsBySelectedCategory = productsBySelectedCategory.reduce((acc, product) => {
        if (!acc[product.brandName]) acc[product.brandName] = [];

        acc[product.brandName].push(product);

        return acc;
    }, {});
    const brands = Object.keys(brandsBySelectedCategory).map((brand) => {
        const products = brandsBySelectedCategory[brand];
        const highestGradeAPrice = Math.max(...products.map((product) => product.GradeAPrice));

        return {
            label: brand,
            discount: highestGradeAPrice
        };
    }).sort((a, b) => b.discount - a.discount);

    // to obtain available Devices based on selected brand
    const devicesBySelectedBrand = brandsBySelectedCategory[page1Values.selectedBrand] || [];
    const devices = devicesBySelectedBrand.map((device) => {
        return {
            label: device.name,
            discount: device.GradeAPrice,
            ...device
        };
    }
    ).sort((a, b) => b.discount - a.discount);

    // to obtain available Storages based on selected device
    // const storages = [{
    //     label: page1Values.selectedDevice.rom,
    //     discount: page1Values.selectedDevice.GradeAPrice
    // }];

    // const storagesBySelectedDevice = devicesBySelectedBrand.filter((device) => device.name === selectedDevice.name);
    // const storages = storagesBySelectedDevice.map((storage) => {
    //     return {
    //         label: storage.rom,
    //         discount: storage.GradeAPrice
    //     };
    // }).sort((a, b) => b.discount - a.discount);

    const fetchStorageList = async (name) => {
        await getStorageList(name).then((res) => {
            const formattedStorageList = res.data.map((storage) => {
                return {
                    label: `${storage.ram ? storage.ram + '/' : ''}${storage.rom}`,
                    ...storage
                };
            });

            setStorageList(formattedStorageList);

            if (formattedStorageList.length <= 0) {
                const deviceName = typeof page1Values.selectedDevice === 'string' ? page1Values.selectedDevice : page1Values.selectedDevice.label;
                const newSelectedDevice = devices.find((device) => device.label === deviceName) || '';

                setTradeInDevice(newSelectedDevice);
            }
        });
    };

    const handleValueChange = (label, value) => {
        switch (label) {
            case 'Select Category':
                setPage1Values({
                    selectedCategory: value,
                    selectedBrand: '',
                    selectedDevice: '',
                    selectedStorage: ''
                });
                setTradeInDevice(null);
                break;

            case 'Select Brand':
                setPage1Values({
                    ...page1Values,
                    selectedBrand: value?.label ?? '',
                    selectedDevice: '',
                    selectedStorage: ''
                });
                setTradeInDevice(null);
                break;

            case 'Select Device': {
                const newSelectedDevice = devices.find((device) => device.label === value?.label) || '';

                setPage1Values({
                    ...page1Values,
                    selectedDevice: newSelectedDevice?.label ?? '',
                    selectedStorage: ''
                });
                setTradeInDevice(null);
                break;
            }

            case 'Select Storage': {
                const deviceName = typeof page1Values.selectedDevice === 'string' ? page1Values.selectedDevice : page1Values.selectedDevice.label;
                const selectedDevice = value ? devices.find((device) => device.label === deviceName) || '' : '';

                setPage1Values({
                    ...page1Values,
                    selectedStorage: value
                });
                setTradeInDevice(selectedDevice);
                break;
            }

            default:
                break;
        }
    };

    useEffect(() => {
        getProductList().then((res) => setCompasiaProductList(res.data)).catch((err) => {
            console.error('Error fetching product list:', err);
            notify('An error occurred! Please try again later.', 'danger');
        });
    }, []);

    useEffect(() => {
        if (page1Values.selectedDevice) {
            const deviceName = typeof page1Values.selectedDevice === 'string' ? page1Values.selectedDevice : page1Values.selectedDevice.label;

            fetchStorageList(deviceName);
        }
    }, [page1Values.selectedDevice]);

    if (!compasiaProductList.length) return (<Spinner color='primary' />);

    return (
        <>
            <CategorySelect categories={categories} selectedCategory={page1Values.selectedCategory} handleValueChange={handleValueChange} />
            <Dropdown label='Select Brand' options={brands} selected={page1Values.selectedBrand}
                handleValueChange={handleValueChange} disabled={!page1Values.selectedCategory} />
            <Dropdown label='Select Device' options={devices} selected={page1Values.selectedDevice}
                handleValueChange={handleValueChange} disabled={!page1Values.selectedBrand} />
            {storageList.length > 0 &&
                <Dropdown label='Select Storage' options={storageList} selected={page1Values.selectedStorage}
                    handleValueChange={handleValueChange} disabled={!page1Values.selectedDevice} />
            }
            {(page1Values.selectedDevice && page1Values.selectedStorage)
                ? <div className={styles.selectedDevice}>
                    <p className={styles.title}>
                        {page1Values.selectedDevice}
                    </p>
                    <p className={styles.content}>
                        <span className={styles.estimate}>
                            Estimated trade-in value
                        </span>
                        <span className={styles.price}>
                            S$
                            {page1Values.selectedStorage?.GradeAPrice.toString().replace(
                                /\B(?=(\d{3})+(?!\d))/g,
                                ','
                            )}
                        </span>
                    </p>
                </div>
                : null
            }
        </>
    );
};

const CategorySelect = ({
    categories,
    selectedCategory,
    handleValueChange
}) => {
    return (
        <div className='w-100'>
            <h4 className='mb-2'>Select Category</h4>
            <div className={styles.categoryButtonGroup}>
                {categories.map((category, index) => {
                    const selected = selectedCategory === category.name;
                    const classes = selected ? `${styles.selected}` : '';

                    return (
                        <MUIButton key={index} variant='outlined' className={classes}
                            disableRipple={true}
                            onClick={() => handleValueChange('Select Category', category.name)}>
                            <img src={category.image} alt={category.name} />
                            <h6>{category.name}</h6>
                            <p>Up to ~S${category.discount}</p>
                        </MUIButton>
                    );
                })}
            </div>
        </div>
    );
};

const Dropdown = ({ label, options, selected, handleValueChange, disabled }) => {
    const value = typeof selected === 'string' ? selected : selected?.label;
    const [inputValue, setInputValue] = React.useState('');
    const openDropDown = {
        'Select Brand': !disabled && !selected,
        'Select Device': !disabled && !selected,
        'Select Storage': !disabled && !selected
    };

    return (
        <fieldset className={styles.dropdown}>
            <h4 className='mb-2'>{label}</h4>
            <Autocomplete
                className={styles.autocomplete}
                disablePortal={true}
                id={label}
                options={options}
                fullWidth={true}
                renderInput={(params) => <TextField {...params} placeholder={`Select a ${label}`} />}
                renderOption={(props, option) => {
                    return (
                        <li {...props}>
                            <span>{option.label}</span>
                            {option?.discount && <span>Up to <span>~${option.discount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span></span>}
                        </li>
                    );
                }}
                value={value}
                onChange={(event, newValue) => handleValueChange(label, newValue)}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                open={openDropDown[label]}
                disabled={disabled} />
        </fieldset>
    );
};

export default Page1;

Page1.propTypes = {
    page1Values: PropTypes.object.isRequired,
    setPage1Values: PropTypes.func.isRequired,
    setTradeInDevice: PropTypes.func.isRequired
};

CategorySelect.propTypes = {
    categories: PropTypes.array.isRequired,
    selectedCategory: PropTypes.string.isRequired,
    handleValueChange: PropTypes.func.isRequired
};

Dropdown.propTypes = {
    label: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    selected: PropTypes.string.isRequired,
    handleValueChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired
};
